import { useEffect, useState } from "react";
import { api, inr } from "@/lib/api";
import {
  TrendingUp, ClipboardList, Users, AlertTriangle,
  Wallet, ArrowUpRight, Package
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

const Kpi = ({ label, value, icon: Icon, accent = "primary", sub, testid }) => (
  <div data-testid={testid} className="border border-border bg-card p-5 rounded-sm relative overflow-hidden group hover:border-primary/40 transition-colors">
    <div className="flex items-start justify-between mb-4">
      <div className={`w-9 h-9 flex items-center justify-center border ${accent === "primary" ? "border-primary/40 bg-primary/10 text-primary" : "border-yellow-500/40 bg-yellow-500/10 text-yellow-500"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{label}</div>
    <div className="font-display font-black text-3xl uppercase tracking-tighter">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [retention, setRetention] = useState(null);
  const [period, setPeriod] = useState("daily");

  useEffect(() => {
    const load = () => api.get(`/analytics/summary?period=${period}`).then(r => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("focus", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); };
  }, [period]);
  useEffect(() => {
    api.get("/analytics/customer-retention").then(r => setRetention(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="p-8 text-muted-foreground">Loading command center...</div>;

  const k = data.kpis;

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2">Command Center</div>
          <h1 className="font-display font-black text-5xl uppercase tracking-tighter">Dashboard</h1>
          <div className="text-sm text-muted-foreground mt-2">Live workshop metrics · updated in real-time</div>
        </div>
        <div className="flex gap-1 border border-border rounded-sm p-1" data-testid="period-toggle">
          {[["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"], ["yearly", "Yearly"]].map(([v, l]) => (
            <button key={v} data-testid={`period-${v}`} onClick={() => setPeriod(v)}
              className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-sm transition-colors ${period === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-4">
        <Kpi testid="kpi-total-revenue" label="Total Revenue" value={inr(k.total_revenue)} icon={TrendingUp} sub={`Job cards ${inr(k.job_card_revenue)} · Sales ${inr(k.sales_revenue)}`} />
        <Kpi testid="kpi-parts-revenue" label="Parts Revenue" value={inr(k.parts_revenue)} icon={Package} sub="Job cards + counter sales billed" />
        <Kpi testid="kpi-labour-revenue" label="Labour Revenue" value={inr(k.labour_revenue)} icon={ClipboardList} sub="Service charges billed" />
        <Kpi testid="kpi-debtors" label="Debtor Balance" value={inr(k.debtor_balance)} icon={Wallet} accent="warning" sub={`Job cards ${inr(k.debtor_job_cards)} · Sales ${inr(k.debtor_sales)}`} />
        <Kpi testid="kpi-creditors" label="Creditor Balance" value={inr(k.creditor_balance)} icon={Wallet} accent="warning" sub="Purchases not yet paid" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi testid="kpi-jobs" label="Total Jobs" value={k.total_jobs} icon={ClipboardList} />
        <Kpi testid="kpi-active" label="Active" value={k.active_jobs} icon={ClipboardList} accent="warning" />
        <Kpi testid="kpi-customers" label="Customers" value={k.total_customers} icon={Users} />
        <Kpi testid="kpi-low-stock" label="Low Stock" value={k.low_stock_count} icon={AlertTriangle} accent="warning" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 border border-border bg-card p-6 rounded-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Revenue</div>
              <h3 className="font-display font-bold text-xl uppercase" data-testid="chart-title">{{ daily: "Today", weekly: "Last 7 Days", monthly: "Last 30 Days", yearly: "This Year" }[period]}</h3>
            </div>
            <div className="font-mono text-primary text-sm">{inr(data.revenue_series.reduce((s, r) => s + r.revenue, 0))}</div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.revenue_series}>
              <CartesianGrid stroke="#27272a" strokeDasharray="2 2" vertical={false} />
              <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272a", borderRadius: 2, fontSize: 12 }} />
              <Line type="monotone" dataKey="revenue" stroke="#FF5C00" strokeWidth={2} dot={{ fill: "#FF5C00", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border bg-card p-6 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Top Selling</div>
          <h3 className="font-display font-bold text-xl uppercase mb-5">Parts</h3>
          {data.top_parts.length === 0 ? (
            <div className="text-sm text-muted-foreground">No parts sold yet</div>
          ) : (
            <div className="space-y-3">
              {data.top_parts.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="text-[10px] w-5 h-5 flex items-center justify-center border border-primary/40 text-primary font-mono">{i+1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{p.qty} units · {inr(p.revenue)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="border border-border bg-card p-6 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Team</div>
          <h3 className="font-display font-bold text-xl uppercase mb-5">Mechanic Performance</h3>
          {data.mechanic_performance.length === 0 ? (
            <div className="text-sm text-muted-foreground">No mechanics registered</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={data.mechanic_performance}>
                <CartesianGrid stroke="#27272a" strokeDasharray="2 2" vertical={false} />
                <XAxis dataKey="name" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272a", borderRadius: 2, fontSize: 12 }} />
                <Bar dataKey="completed" fill="#FF5C00" />
                <Bar dataKey="total_jobs" fill="#FFB800" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="border border-border bg-card p-6 rounded-sm">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Alerts</div>
          <h3 className="font-display font-bold text-xl uppercase mb-5 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" /> Low Stock
          </h3>
          {data.low_stock.length === 0 ? (
            <div className="text-sm text-muted-foreground">All parts well-stocked</div>
          ) : (
            <div className="space-y-2">
              {data.low_stock.map(p => (
                <div key={p.id} className="flex items-center justify-between border-b border-border pb-2">
                  <div>
                    <div className="text-sm">{p.name}</div>
                    <div className="text-[10px] text-muted-foreground font-mono uppercase">{p.sku}</div>
                  </div>
                  <div className="text-yellow-500 font-mono text-sm bg-yellow-500/10 px-2 py-1 rounded-sm">{p.stock} left</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {retention && (
        <div className="border border-border bg-card p-6 rounded-sm">
          <h3 className="font-display font-bold text-xl uppercase mb-4">Customer Retention</h3>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Billed customers</div>
              <div className="font-display font-black text-3xl">{retention.total_customers_billed}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Repeat</div>
              <div className="font-display font-black text-3xl">{retention.repeat_customers}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Rate</div>
              <div className="font-display font-black text-3xl text-primary">{retention.retention_rate}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
